import React from 'react';
import { View, Text, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import { IColumnConfig, IFilterModel } from '@/types/admin/dataTable';
import { DESKTOP_STYLES, DESKTOP_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface DesktopFilterRowProps<T> {
  index: number;
  filter: { field: string; operator: string; value: any };
  columnByKey: Record<string, IColumnConfig<T>>;
  filterableColumns: IColumnConfig<T>[];
  operatorOptions: Array<{ label: string; value: string }>;
  onChangeField: (index: number, fieldKey: string) => void;
  onChangeOperator: (index: number, operator: string) => void;
  onChangeValue: (index: number, value: any) => void;
  onRemove: (index: number) => void;
  onOpenColumnPicker: (index: number) => void;
  onOpenOperatorPicker: (index: number) => void;
  onOpenValuePicker: (index: number) => void;
}

export const DesktopFilterRow = <T,>({
  index,
  filter,
  columnByKey,
  filterableColumns,
  operatorOptions,
  onChangeField,
  onChangeOperator,
  onChangeValue,
  onRemove,
  onOpenColumnPicker,
  onOpenOperatorPicker,
  onOpenValuePicker,
}: DesktopFilterRowProps<T>) => {
  const currentColumn = columnByKey[filter.field];

  return (
    <View style={styles.filterRow}>
      <View style={styles.filterField}>
        <Text style={[styles.filterLabel, DESKTOP_STYLES.secondaryText]}>Column</Text>
        <TouchableOpacity
          style={[styles.filterInput, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}
          onPress={() => onOpenColumnPicker(index)}
        >
          <Text style={DESKTOP_STYLES.primaryText}>{currentColumn?.label || filter.field}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.filterField}>
        <Text style={[styles.filterLabel, DESKTOP_STYLES.secondaryText]}>Operator</Text>
        <TouchableOpacity
          style={[styles.filterInput, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}
          onPress={() => onOpenOperatorPicker(index)}
        >
          <Text style={DESKTOP_STYLES.primaryText}>{operatorOptions.find(o => o.value === filter.operator)?.label || filter.operator}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.filterField}>
        <Text style={[styles.filterLabel, DESKTOP_STYLES.secondaryText]}>Value</Text>
        {currentColumn?.filterType === 'date' ? (
          <TextInput
            style={[styles.filterInput, DESKTOP_STYLES.primaryText]}
            value={filter.value}
            onChangeText={(value) => onChangeValue(index, value)}
            placeholder="YYYY-MM-DD"
          />
        ) : currentColumn?.filterType === 'select' || currentColumn?.filterType === 'multiSelect' || currentColumn?.filterType === 'boolean' ? (
          <TouchableOpacity
            style={[styles.filterInput, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}
            onPress={() => onOpenValuePicker(index)}
          >
            <Text style={DESKTOP_STYLES.primaryText}>{String(filter.value ?? '') || 'Select value'}</Text>
          </TouchableOpacity>
        ) : (
          <TextInput
            style={[styles.filterInput, DESKTOP_STYLES.primaryText]}
            value={filter.value}
            onChangeText={(value) => onChangeValue(index, value)}
            placeholder="Enter value"
          />
        )}
      </View>

      <TouchableOpacity onPress={() => onRemove(index)} style={styles.removeFilterButton}>
        <SVG_COMPONENTS.CANCEL_X width={16} height={16} color={DESKTOP_COLORS.TABLE.TEXT_SECONDARY} />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  filterRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 12, marginBottom: 12, width: '100%' },
  filterField: { flex: 1, position: 'relative' },
  filterLabel: { fontSize: 12, marginBottom: 4 },
  filterInput: {
    borderWidth: 1,
    borderColor: DESKTOP_COLORS.TABLE.BORDER,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    backgroundColor: DESKTOP_COLORS.TABLE.BACKGROUND,
  },
  removeFilterButton: { padding: 8, marginBottom: 2 },
});


